// src/features/legal/LegalComponents.jsx
// Reusable disclaimer UI. Every data-displaying page uses these.
//
// Components:
//   <PersistentDisclaimerBar />  — shown on EVERY screen after onboarding (new)
//   <AccuracyBanner type= />     — shown beneath every alert/data section (new)
//   <ContextualNotice type= />   — one-line dismissible page banner
//   <SOSDisclaimer />            — SOS flow warning
//   <SMSDisclaimer />            — SMS enrollment warning
//   <OfficialSourcesNote />      — official sources footer
//   <NotMonitored24_7 />         — compact monitoring status
//   <CommunityDataBadge />       — inline COMMUNITY tag
//   <TermsLink />                — link to /legal

import { useLangStore } from '@store/lang';
import { useState } from 'react';
import {
  CONTEXTUAL_NOTICES,
  DATA_DISCLAIMER,
  ACCURACY_DISCLAIMER,
} from '@data/legal';
import { T } from '@components/theme';

// ── PersistentDisclaimerBar ───────────────────────────────────────
// Shown on EVERY screen after the onboarding gate.
// Cannot be permanently dismissed — reappears each session.
// Collapses to a thin strip after the user taps it once per session.
export function PersistentDisclaimerBar() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{
      background: 'rgba(127,29,29,0.15)',
      borderBottom: '1px solid rgba(220,38,38,0.20)',
      flexShrink: 0,
    }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%', background: 'none', border: 'none',
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '5px 12px', cursor: 'pointer', fontFamily: T.font,
          textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 11, flexShrink: 0 }}>⚠️</span>
        <span style={{
          flex: 1, fontSize: 9.5, color: '#fca5a5', fontWeight: 700,
          lineHeight: 1.3,
        }}>
          {ACCURACY_DISCLAIMER.short}
        </span>
        <span style={{ fontSize: 10, color: '#fca5a5', opacity: 0.6, flexShrink: 0 }}>
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {expanded && (
        <div style={{
          padding: '0 12px 10px',
          fontSize: 10, color: '#fca5a5', lineHeight: 1.6,
        }}>
          <p style={{ margin: '0 0 8px' }}>{ACCURACY_DISCLAIMER.medium}</p>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {DATA_DISCLAIMER.officialSources.map(s => (
              <a key={s.url} href={s.url} target="_blank" rel="noreferrer"
                style={{
                  fontSize: 9, color: '#fca5a5',
                  background: 'rgba(220,38,38,0.15)',
                  border: '1px solid rgba(220,38,38,0.3)',
                  padding: '2px 7px', borderRadius: 4,
                  textDecoration: 'none', fontWeight: 700,
                }}>
                {s.name} →
              </a>
            ))}
            <a href="/legal" style={{
              fontSize: 9, color: '#fca5a5', textDecoration: 'underline',
            }}>
              Full Terms of Service
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

// ── AccuracyBanner ────────────────────────────────────────────────
// Shown beneath every section that displays potentially relied-upon data.
// type: 'nws' | 'medical' | 'evacuation' | 'gas' | 'shelter' | 'general'
// Non-dismissible — always present under data sections.
const BANNER_TEXT = {
  nws:        ACCURACY_DISCLAIMER.nwsSpecific,
  medical:    ACCURACY_DISCLAIMER.medicalSpecific,
  evacuation: ACCURACY_DISCLAIMER.evacuationSpecific,
  gas:        ACCURACY_DISCLAIMER.gasSpecific,
  shelter:    ACCURACY_DISCLAIMER.shelterSpecific,
  general:    ACCURACY_DISCLAIMER.medium,
};

export function AccuracyBanner({ type = 'general' }) {
  const [expanded, setExpanded] = useState(false);
  const text = BANNER_TEXT[type] ?? BANNER_TEXT.general;
  return (
    <div style={{
      margin: '6px 14px 0',
      background: 'rgba(107,114,128,0.06)',
      border: '1px solid rgba(107,114,128,0.15)',
      borderRadius: 7,
      overflow: 'hidden',
    }}>
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          width: '100%', padding: '5px 10px',
          background: 'none', border: 'none',
          display: 'flex', alignItems: 'center', gap: 6,
          cursor: 'pointer', fontFamily: T.font, textAlign: 'left',
        }}
      >
        <span style={{ fontSize: 10, flexShrink: 0 }}>ℹ️</span>
        <span style={{ fontSize: 9, color: '#9ca3af', flex: 1 }}>
          Community reports may be unverified
        </span>
        <span style={{ fontSize: 9, color: '#6b7280' }}>{expanded ? '▲' : '▼'}</span>
      </button>
      {expanded && (
        <div style={{ padding: '0 10px 8px', fontSize: 9, color: '#9ca3af', lineHeight: 1.6 }}>
          {text}{' '}
          <a href="/legal" style={{ color: '#6b7280', textDecoration: 'underline' }}>
            Terms of Service
          </a>
        </div>
      )}
    </div>
  );
}

// ── ContextualNotice ──────────────────────────────────────────────
// One-line dismissible notice shown below the header of relevant pages.
export function ContextualNotice({ type, style: extraStyle }) {
  const [dismissed, setDismissed] = useState(false);
  const text = CONTEXTUAL_NOTICES[type];
  if (!text || dismissed) return null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '7px 12px',
      background: 'rgba(217,119,6,0.08)',
      borderBottom: '1px solid rgba(217,119,6,0.15)',
      flexShrink: 0,
      ...extraStyle,
    }}>
      <span style={{ fontSize: 12 }}>⚠️</span>
      <div style={{ flex: 1, fontSize: 10, color: '#92400e', fontWeight: 600, lineHeight: 1.4 }}>
        {text}
      </div>
      <button
        onClick={() => setDismissed(true)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          fontSize: 14, color: '#92400e', padding: '0 2px',
          fontFamily: T.font, flexShrink: 0, opacity: 0.6,
        }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

// ── SOSDisclaimer ─────────────────────────────────────────────────
export function SOSDisclaimer() {
  return (
    <div style={{
      margin: '0 16px 12px',
      padding: '12px 14px',
      background: 'rgba(220,38,38,0.08)',
      border: '1px solid rgba(220,38,38,0.25)',
      borderRadius: 10,
    }}>
      <a href="tel:911" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, width: '100%', padding: '10px',
        background: '#dc2626', borderRadius: 8,
        textDecoration: 'none', marginBottom: 10,
      }}>
        <span style={{ fontSize: 18 }}>📞</span>
        <span style={{ fontSize: 14, fontWeight: 900, color: '#fff' }}>
          Call 911 for emergencies
        </span>
      </a>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {[
          '🚫  SOS does NOT reach 911 or emergency services',
          '📵  Not monitored 24/7 — no guaranteed response',
          '👥  Your report goes to neighbors, not dispatchers',
          'ℹ️  Not a substitute for professional emergency services',
        ].map(line => (
          <div key={line} style={{ fontSize: 11, color: '#fca5a5', fontWeight: 600, lineHeight: 1.4 }}>
            {line}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SMSDisclaimer ─────────────────────────────────────────────────
export function SMSDisclaimer({ compact = false }) {
  if (compact) {
    return (
      <div style={{ fontSize: 10, color: '#6b7280', lineHeight: 1.5, padding: '4px 0' }}>
        ⚠ SMS to this number is NOT a 911 service and is not monitored 24/7.
        Standard message rates may apply. Reply STOP to cancel.
      </div>
    );
  }
  return (
    <div style={{
      padding: '10px 12px',
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: 9, marginTop: 8,
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', marginBottom: 4 }}>
        About SMS alerts
      </div>
      <div style={{ fontSize: 11, color: '#6b7280', lineHeight: 1.6 }}>
        • This is <strong style={{ color: '#9ca3af' }}>not a 911 service</strong> — SMS does not alert emergency services<br />
        • <strong style={{ color: '#9ca3af' }}>Not monitored 24/7</strong> by any staff<br />
        • Delivery not guaranteed during disasters<br />
        • Standard message rates may apply · Reply STOP to cancel
      </div>
    </div>
  );
}

// ── NotMonitored24_7 ──────────────────────────────────────────────
export function NotMonitored24_7() {
  const { lang } = useLangStore();
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '6px 14px',
      background: 'rgba(0,0,0,0.3)',
      borderTop: '1px solid rgba(255,255,255,0.06)',
    }}>
      <span style={{ fontSize: 12 }}>📵</span>
      <span style={{ fontSize: 10, color: '#6b7280' }}>
        {lang==='es'?'No monitoreado 24/7 · No es servicio de emergencia · No es asesoramiento profesional · Llame al 911':'Not monitored 24/7 · Not an emergency service · Not professional advice · Call 911'}
      </span>
    </div>
  );
}

// ── OfficialSourcesNote ───────────────────────────────────────────
export function OfficialSourcesNote({ sources } = {}) {
  const { lang } = useLangStore();
  const list = sources ?? DATA_DISCLAIMER.officialSources;
  return (
    <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6, fontWeight: 700 }}>
        {lang==='es'?'Para información oficial:':'For official information:'}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {list.map(s => (
          <a key={s.url} href={s.url} target="_blank" rel="noreferrer"
            style={{
              fontSize: 10, color: '#60a5fa', textDecoration: 'none', fontWeight: 600,
              background: 'rgba(96,165,250,0.08)',
              padding: '3px 8px', borderRadius: 5,
              border: '1px solid rgba(96,165,250,0.2)',
            }}>
            {s.name} →
          </a>
        ))}
      </div>
    </div>
  );
}

// ── CommunityDataBadge ────────────────────────────────────────────
export function CommunityDataBadge() {
  return (
    <span style={{
      fontSize: 8, fontWeight: 700, color: '#92400e',
      background: 'rgba(217,119,6,0.12)',
      border: '1px solid rgba(217,119,6,0.2)',
      padding: '1px 5px', borderRadius: 3,
      verticalAlign: 'middle',
    }}>
      COMMUNITY
    </span>
  );
}

// ── TermsLink ─────────────────────────────────────────────────────
export function TermsLink({ style: extraStyle } = {}) {
  return (
    <a href="/legal" style={{ fontSize: 10, color: '#6b7280', textDecoration: 'underline', ...extraStyle }}>
      Terms of Service
    </a>
  );
}
